const enterVariantPrefix = "cl-v-anim--enter-";
const hoverVariantPrefix = "cl-v-anim--hover-";
const enterActiveClass = "cl-anim--enter-active";
const hoverActiveClass = "cl-anim--hover-active";
const hoverLeavingClass = "cl-anim--hover-leaving";
const hoverTumbleVariant = `${hoverVariantPrefix}tumble`;
const enterAnimationName = "cl-anim-enter";
const hoverTumbleRotationProperty = "--cl-anim-hover-tumble-rotate";
const hoverTumbleRotateXProperty = "--cl-anim-hover-target-rotate-x";
const hoverTumbleRotateYProperty = "--cl-anim-hover-target-rotate-y";

const observedEnterElements = new WeakSet<Element>();
const initializedHoverElements = new WeakSet<Element>();
let enterObserver: IntersectionObserver = null;

function hasVariant(element: Element, prefix: string): boolean {
	return Array.from(element.classList).some(className => className.startsWith(prefix));
}

function setTumbleRotation(element: HTMLElement, bounds: DOMRect, maxRotation: number, clientX: number, clientY: number): void {
	if (bounds.width <= 0 || bounds.height <= 0) return;

	const x = Math.min(Math.max((clientX - bounds.left) / bounds.width, 0), 1);
	const y = Math.min(Math.max((clientY - bounds.top) / bounds.height, 0), 1);
	element.style.setProperty(hoverTumbleRotateXProperty,
		`${((y * 2 - 1) * maxRotation).toFixed(2)}deg`);
	element.style.setProperty(hoverTumbleRotateYProperty,
		`${((1 - x * 2) * maxRotation).toFixed(2)}deg`);
}

function clearTumbleRotation(element: HTMLElement): void {
	element.style.removeProperty(hoverTumbleRotateXProperty);
	element.style.removeProperty(hoverTumbleRotateYProperty);
}

function getEnterObserver(): IntersectionObserver {
	enterObserver ??= new IntersectionObserver(entries => {
		for (const entry of entries) {
			if (!entry.isIntersecting && entry.intersectionRatio <= 0) continue;

			enterObserver.unobserve(entry.target);
			if (!hasVariant(entry.target, enterVariantPrefix)) {
				observedEnterElements.delete(entry.target);
				continue;
			}

			const onAnimationEnd = (event: Event): void => {
				const animationEvent = event as AnimationEvent;
				if (event.target !== entry.target || animationEvent.animationName !== enterAnimationName) return;
				entry.target.classList.remove(enterActiveClass);
				entry.target.removeEventListener("animationend", onAnimationEnd);
			};
			entry.target.addEventListener("animationend", onAnimationEnd);
			entry.target.classList.add(enterActiveClass);
		}
	});
	return enterObserver;
}

function initializeEnterAnimation(element: Element): void {
	if (observedEnterElements.has(element)) return;
	observedEnterElements.add(element);
	getEnterObserver().observe(element);
}

function initializeHoverAnimation(element: HTMLElement): void {
	if (initializedHoverElements.has(element)) return;

	// Some visual owners intentionally route pointer state through their rendered parent.
	const eventTarget = element.classList.contains("mdln-sys-event-parent")
		? element.parentElement
		: element;
	if (!eventTarget) return;
	initializedHoverElements.add(element);
	let tumbleBounds: DOMRect = null;
	let tumbleFrame = 0;
	let tumbleClientX = 0;
	let tumbleClientY = 0;
	let tumbleMaxRotation = 0;
	const resetTumble = (): void => {
		if (tumbleFrame) cancelAnimationFrame(tumbleFrame);
		tumbleFrame = 0;
		tumbleBounds = null;
		tumbleMaxRotation = 0;
		clearTumbleRotation(element);
	};

	const leaveTarget = eventTarget.parentElement?.classList.contains("cl-block")
		? eventTarget.parentElement
		: eventTarget;
	eventTarget.addEventListener("mouseenter", () => {
		resetTumble();
		if (!hasVariant(element, hoverVariantPrefix)) {
			element.classList.remove(hoverActiveClass, hoverLeavingClass);
			return;
		}
		if (element.classList.contains(hoverTumbleVariant)) {
			// Resolve the local style value once so pointer frames remain layout-free.
			tumbleBounds = eventTarget.getBoundingClientRect();
			tumbleMaxRotation = Number.parseFloat(window.getComputedStyle(element)
				.getPropertyValue(hoverTumbleRotationProperty)) || 0;
		}
		element.classList.remove(hoverLeavingClass);
		element.classList.add(hoverActiveClass);
	});
	eventTarget.addEventListener("mousemove", event => {
		if (!element.classList.contains(hoverTumbleVariant)) {
			resetTumble();
			return;
		}
		if (!element.classList.contains(hoverActiveClass) || !tumbleBounds) return;

		const mouseEvent = event as MouseEvent;
		tumbleClientX = mouseEvent.clientX;
		tumbleClientY = mouseEvent.clientY;
		if (tumbleFrame) return;

		// Coalesce high-frequency pointer input without rereading transformed geometry.
		tumbleFrame = requestAnimationFrame(() => {
			tumbleFrame = 0;
			if (!element.classList.contains(hoverTumbleVariant)
				|| !element.classList.contains(hoverActiveClass) || !tumbleBounds) return;
			setTumbleRotation(element, tumbleBounds, tumbleMaxRotation, tumbleClientX, tumbleClientY);
		});
	});
	leaveTarget.addEventListener("mouseleave", () => {
		resetTumble();
		if (!element.classList.contains(hoverActiveClass)) return;
		if (!hasVariant(element, hoverVariantPrefix)) {
			element.classList.remove(hoverActiveClass, hoverLeavingClass);
			return;
		}
		element.classList.add(hoverLeavingClass);
		element.classList.remove(hoverActiveClass);
	});
	element.addEventListener("transitionend", event => {
		const propertyName = (event as TransitionEvent).propertyName;
		if (event.target !== element
			|| (propertyName !== "filter" && !propertyName.startsWith("--cl-anim-hover-"))) return;
		element.classList.remove(hoverLeavingClass);
	});
}

/** Initializes stored Collage animation variants, including newly rendered PLE content. */
export function initAnimations(root: ParentNode = document): void {
	root.querySelectorAll(`[class*="${enterVariantPrefix}"]`).forEach(initializeEnterAnimation);
	root.querySelectorAll<HTMLElement>(`[class*="${hoverVariantPrefix}"]`).forEach(initializeHoverAnimation);
}
